import type { GpuServer, GpuServerStatus } from '../../../types/models/GpuServer';
import { formatDateTime } from '../../../utils/dateFormat';

// Customer-facing presentation of a GPU server's status (docs/api-types/gpu-servers.md "Statuses").
// The strings are the agreed customer copy: honest about billing and data loss, and never promising a
// boot time, a low-balance warning, a refund on delete or a top-up rescue of an `unpaid` server.

export type GpuServerLabelColor = 'blue' | 'green' | 'red' | 'orange' | 'grey';

interface StatusPresentation {
  label: string;
  color: GpuServerLabelColor;
}

const STATUS_PRESENTATION: Record<GpuServerStatus, StatusPresentation> = {
  starting: { label: 'Starting', color: 'blue' },
  // A guest-OS shutdown still reads `running` and still bills: never "Up" or "Online".
  running: { label: 'Active', color: 'green' },
  // Normally visible for seconds or minutes: the delete is claimed in the same meter run.
  unpaid: { label: 'Being deleted', color: 'red' },
  deleting: { label: 'Being deleted', color: 'orange' },
  deleted: { label: 'Deleted', color: 'grey' },
  failed: { label: 'Could not be created', color: 'red' },
  unavailable: { label: 'Unavailable', color: 'red' },
};

const isKnownStatus = (status: string): status is GpuServerStatus =>
  Object.prototype.hasOwnProperty.call(STATUS_PRESENTATION, status);

/** Label text + colour for a status; an unknown status renders as-is, in grey. */
export function gpuServerStatusLabel(status: string): StatusPresentation {
  return isKnownStatus(status) ? STATUS_PRESENTATION[status] : { label: status, color: 'grey' };
}

/** Changes at the next meter tick(s): poll while any displayed server is in one of these. */
export function isTransient(status: string): boolean {
  return status === 'starting' || status === 'deleting' || status === 'unpaid';
}

/** Never changes again. Every other status (including an unknown one) counts as active. */
export function isTerminal(status: string): boolean {
  return status === 'deleted' || status === 'failed' || status === 'unavailable';
}

/** Offer Delete. A `starting` server may still be refused ("still starting") — the backend decides. */
export function isDeletable(status: string): boolean {
  return status === 'starting' || status === 'running' || status === 'unpaid';
}

/** Its next hour will be debited from the balance (what the low-balance estimate sums). */
export function isBilled(status: string): boolean {
  return status === 'running' || status === 'unpaid' || status === 'starting';
}

/** The customer copy for the server's current status; `null` for an unknown status. */
export function gpuServerStatusCopy(
  server: Pick<GpuServer, 'status' | 'started_at' | 'destroy_reason' | 'destroyed_at'>,
): string | null {
  switch (server.status) {
    case 'starting':
      return `Your server is being created. This usually takes a few minutes, up to 30. Billing started at ${formatDateTime(server.started_at)}.`;
    case 'running':
      return 'Active — billed hourly.';
    case 'unpaid':
      return 'Being deleted: your balance could not pay the current hour.';
    case 'deleting':
      return 'Being deleted.';
    case 'deleted': {
      const on = server.destroyed_at !== null ? ` on ${formatDateTime(server.destroyed_at)}` : '';
      if (server.destroy_reason === 'customer') {
        return `Deleted by you${on}.`;
      }
      if (server.destroy_reason === 'unpaid') {
        return `Deleted${on}: the balance could not pay the next hour.`;
      }
      return `Deleted${on}.`;
    }
    case 'failed':
      return 'This server could not be created. Every hour charged for it is refunded to your balance.';
    case 'unavailable':
      return 'This server is no longer available. Our team has been alerted — contact support.';
    default:
      return null;
  }
}

const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

/**
 * Until when the disk of a deleted server is kept: `destroyed_at + 96 h − 15 min`, rounded down to
 * the minute (epoch ms). The 96 h window really starts at the delete request, one to three meter
 * ticks before `destroyed_at`, so the margin keeps us from promising more than the trash holds.
 * `null` when `destroyed_at` is missing or unparseable.
 */
export function diskKeptUntil(destroyedAt: string | null): number | null {
  if (destroyedAt === null) {
    return null;
  }
  const ts = Date.parse(destroyedAt);
  if (Number.isNaN(ts)) {
    return null;
  }
  const until = ts + 96 * HOUR_MS - 15 * MINUTE_MS;
  return Math.floor(until / MINUTE_MS) * MINUTE_MS;
}

/**
 * The disk sentence shown after a deletion, or `null` when there is none: only a server deleted by
 * the customer or for non-payment has a guaranteed 96 h trash (an external delete has no promise).
 */
export function diskSentence(
  server: Pick<GpuServer, 'status' | 'destroy_reason' | 'destroyed_at'>,
  now: number = Date.now(),
): string | null {
  if (server.status !== 'deleted' || (server.destroy_reason !== 'customer' && server.destroy_reason !== 'unpaid')) {
    return null;
  }
  const until = diskKeptUntil(server.destroyed_at);
  if (until === null) {
    return null;
  }
  if (now < until) {
    return `Its disk is kept until ${formatDateTime(new Date(until).toISOString())}: contact support before then if you need it back. After that it is permanently erased.`;
  }
  return 'Its disk has been permanently erased.';
}
